import { ContractUnionWorkTypes } from '../constants/contractUnionWorkTypes';

interface Location {
    readonly lat: number;
    readonly lng: number;
}

interface MapOptions {
    readonly mapId: string;
    readonly apiKey: string;
}

/** A US county record with optional group/work-type detail. */
interface UsCounty {
    id: number;
    name: string;
    usCountyGroups?: { contractUnionWorkTypes: number[] }[];
    workTypeName?: string;
    displayName?: string;
    [key: string]: unknown;
}

/** Address shape consumed by the county helpers. */
interface CountyAddress {
    usCountyId?: number | null;
    jobLocationCountyId?: number | null;
    contractUnionWorkTypeId?: number | null;
    parentCountyName?: string | null;
    subCountyName?: string | null;
}

/**
 * Returns a map image from an address
 */
export const getStaticMapUrlForAddress = (address: Location, options: MapOptions): string => {
    const { lat, lng } = address;
    const { mapId, apiKey } = options;

    const defaults = {
        zoom: 18,
        size: '280x210',
        markerColor: 'red',
        scale: 2,
    };

    const markers = `color:${defaults.markerColor}|${lat},${lng}`;
    return `https://maps.googleapis.com/maps/api/staticmap?scale=${defaults.scale}&zoom=${defaults.zoom}&size=${defaults.size}&markers=${encodeURIComponent(markers)}&key=${apiKey}&mapId=${mapId}`;
};

/**
 * Resolve the county record for an address, attaching a `displayName` and
 * (when a work type is set) filtering `usCountyGroups` and setting `workTypeName`.
 * Returns null when the address has no county reference or no match is found.
 */
export const getCounty = (usCounties: UsCounty[], address: CountyAddress | null): UsCounty | null => {
    if (!address || (!address.usCountyId && !address.jobLocationCountyId)) {
        return null;
    }

    const usCounty = usCounties.find(

        (c) => c.id == (address.usCountyId ?? address.jobLocationCountyId),
    );

    if (!usCounty) {
        return null; // Return null if the county is not found
    }

    if (address.contractUnionWorkTypeId) {
        usCounty.usCountyGroups = usCounty.usCountyGroups?.filter(
            (grp) => grp.contractUnionWorkTypes.includes(address.contractUnionWorkTypeId as number),
        );
        usCounty.workTypeName = ContractUnionWorkTypes.find(
            (x) => x.value === address.contractUnionWorkTypeId,
        )?.label;
    }

    // If `parentCountyName` and `subCountyName` are provided, format the display name
    if (address.parentCountyName && address.subCountyName) {
        return {
            ...usCounty,
            displayName: `${address.parentCountyName} (${address.subCountyName})`,
        };
    }

    // Fallback to the county's name
    return {
        ...usCounty,
        displayName: usCounty.name,
    };
};

/**
 * Return an "Original County" suffix string for an address's job-location county,
 * or '' when there is no county reference or match.
 */
export const getCountyName = (usCounties: UsCounty[], address: CountyAddress | null): string => {
    if (!address || (!address.jobLocationCountyId && !address.usCountyId)) {
        return '';
    }

    // The "Original County" reflects the job location's county, not the
    // currently-selected/adjusted county (usCountyId).
    const usCounty = usCounties.find(

        (c) => c.id == (address.jobLocationCountyId ?? address.usCountyId),
    );

    if (!usCounty) {
        return ''; // Return empty string if county is not found
    }

    // Check for sub-county details
    if (address.parentCountyName && address.subCountyName) {
        return ` (Original County: ${address.parentCountyName} (${address.subCountyName}))`;
    }

    // Fallback to just the county name
    return ` (Original County: ${usCounty.name})`;
};